import { Button, Col, Flex, Row, Select, Skeleton, Spin, Table, Typography, Modal, Tag, message, Avatar, Tooltip, Space} from 'antd';
import { HiOutlineUpload } from 'react-icons/hi';
import { FaEdit, FaEye, FaTrashAlt, FaClock } from 'react-icons/fa';
import { useRecoilValue } from 'recoil';
import { themeState } from '../../atom';
import CustomCard from '../../components/Card';
import { useEffect, useState } from 'react';
import ApiService from '../../APIServices/ApiService';
import moment from 'moment';
import AffiliatePopup from './AffiliatePopup';
import QRCode from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import ExtendSubscriptionModal from './ExtendSubscriptionModal';
import PageTourWrapper from '../../components/PageTourWrapper';
import { TOUR_PAGES } from '../../Utils/TourConfig';

const AffiliateManagement = () => {
    const navigate = useNavigate();
    const theme = useRecoilValue(themeState);
    const [affiliates, setAffiliates] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [broadcasting, setBroadcasting] = useState(null);
    const [selectedAffiliate, setSelectedAffiliate] = useState(null);
    const [popupType, setPopupType] = useState('Add');
    const [categories, setCategories] = useState([]);
    const [qrModalVisible, setQrModalVisible] = useState(false);
    const [selectedQrUrl, setSelectedQrUrl] = useState('');
    const [extendModalVisible, setExtendModalVisible] = useState(false);
    const [selectedAffiliateForExtend, setSelectedAffiliateForExtend] = useState(null);

    const [payoutsByAffiliate, setPayoutsByAffiliate] = useState({});
    /**
     * One approved link slug per affiliate, for the QR column.
     *
     * The guides page reads its route param as a LINK SLUG — `const { id: slug }
     * = useParams()` — and resolves it through /affiliate-links/by-slug. The QR
     * was built from the affiliate's ObjectId, so every one of them pointed at
     * a slug that does not exist and scanned to "Link unavailable".
     */
    const [linkSlugs, setLinkSlugs] = useState({});
    const [linkCounts, setLinkCounts] = useState({});

    useEffect(() => {
        ApiService.getAllCategories()
            .then((response) => setCategories(response))
            .catch(() => message.error('Failed to fetch categories.'));
        fetchAffiliates();
    }, []);

    const fetchAffiliates = async () => {
        setTableLoading(true);
        try {
            const [affs, summary, allLinks] = await Promise.all([
                ApiService.getAllAffiliateSubscriptions(),
                // The one commission ledger, same rows the Affiliate Programme
                // console reads — so the two screens cannot show this
                // affiliate a different lifetime figure.
                ApiService.getProgrammeAffiliates().catch(() => ({ affiliates: [] })),
                ApiService.getAllAffiliateLinks({ limit: 1000 }).catch(() => ({ links: [] })),
            ]);
            setAffiliates(affs || []);
            const byAff = {};
            (summary?.affiliates || []).forEach((s) => { byAff[s.id] = s; });
            setPayoutsByAffiliate(byAff);
            const counts = {};
            const slugs = {};
            (allLinks?.links || []).forEach((l) => {
                if (l.status === 'approved') {
                    const id = l.affiliateId?._id || l.affiliateId;
                    counts[id] = (counts[id] || 0) + 1;
                    // The first approved link wins. An affiliate with several
                    // has no "primary" one, and picking a different one on
                    // every reload would change a QR code somebody printed.
                    if (!slugs[id] && l.slug) slugs[id] = l.slug;
                }
            });
            setLinkCounts(counts);
            setLinkSlugs(slugs);
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to fetch affiliates.');
            setAffiliates([]);
        } finally {
            setTableLoading(false);
        }
    };

    const deleteAffiliate = async (affiliateId) => {
        setTableLoading(true);
        try {
            await ApiService.deleteAffiliateSubsubscription(affiliateId);
            message.success('Affiliate deleted successfully.');
            setAffiliates((prev) => prev.filter((a) => a._id !== affiliateId));
        } catch (err) {
            message.error('Failed to delete affiliate.');
        } finally {
            setTableLoading(false);
        }
    };

    const confirmDelete = (affiliateId) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this affiliate?',
            onOk: () => deleteAffiliate(affiliateId),
            okText: 'Yes',
            cancelText: 'No',
        });
    };

    const handleView = (affiliate) => {
        window.open("#/affiliate-guides/" + affiliate._id, '_blank');
    };

    const handleHotelManagement = (affiliateId) => {
        navigate(`/hotel-management/${affiliateId}`);
    };

    /** The guides page for an affiliate, or null when they have no live link. */
    const guidesUrl = (affiliateId) => {
        const slug = linkSlugs[affiliateId];
        return slug ? `${window.location.origin}/#/affiliate-guides/${slug}` : null;
    };

    const handleQrCodeClick = (affiliateId) => {
        const url = guidesUrl(affiliateId);
        if (!url) return;
        setSelectedQrUrl(url);
        setQrModalVisible(true);
    };

    const handleExtendSubscription = (affiliate) => {
        setSelectedAffiliateForExtend(affiliate);
        setExtendModalVisible(true);
    };

    const onExtendSuccess = (updatedAffiliate) => {
        // Update the affiliate in the list
        setAffiliates(prev => prev.map(aff =>
            aff._id === updatedAffiliate._id ? { ...aff, ...updatedAffiliate } : aff
        ));
        setExtendModalVisible(false);
    };

    const columns = [
        {
            title: 'Affiliate',
            key: 'affiliate',
            width: 150,
            render: (_, record) => (
                <Flex vertical align="center" gap={8}>
                    {record.logo ? (
                        <Avatar style={{ width: 140, height: 40 }} src={record.logo} shape="square" />
                    ) : (
                        <Avatar style={{ width: 80, height: 80 }} shape="square">N/A</Avatar>
                    )}
                    <Typography.Text strong style={{ textAlign: 'center', color: 'black', marginTop: '-5px' }}>{record.affiliateName}</Typography.Text>
                </Flex>
            ),
        },
        {
            title: 'QR Code',
            key: 'qrCode',
            width: 80,
            className: 'affiliate-qr-column',
            render: (_, record) => {
                const url = guidesUrl(record._id);
                // No approved link means there is no page to point at. A QR
                // that scans to an error is worse than none: somebody prints
                // it before anyone discovers it leads nowhere.
                if (!url) {
                    return (
                        <Tooltip title="This affiliate has no approved link yet, so there is no page to link to.">
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>No link</Typography.Text>
                        </Tooltip>
                    );
                }
                return (
                    <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleQrCodeClick(record._id)}
                        title="Click to enlarge QR code"
                    >
                        <QRCode value={url} size={80} level="M" includeMargin={true} />
                    </div>
                );
            }
        },
        {
            title: 'Color',
            dataIndex: 'primaryColor',
            key: 'primaryColor',
            width: 40,
            render: (color) => <Tag color={color}>{color}</Tag>
        },
        {
            title: 'Parent',
            key: 'parent',
            width: 140,
            render: (_, r) => {
                const parent = (affiliates || []).find((a) => a._id === (r.parentAffiliateId?._id || r.parentAffiliateId));
                return parent ? <Tag color="purple">{parent.affiliateName}</Tag> : <Typography.Text type="secondary">—</Typography.Text>;
            },
        },
        {
            // ONE commission column, labelled.
            //
            // This was three: "Website commission" showing a bare `20% / 20% /
            // 5%` whose meaning lived only in a tooltip, plus separate "Own
            // page %" and "Sub %". Three numbers with no names is not a rate
            // card — the reader had to hover to learn which was which, and the
            // two legacy percentages read as if they belonged to the same
            // scheme as the first three, which they do not.
            title: 'Commission',
            key: 'rates',
            width: 230,
            render: (_, r) => {
                const rates = payoutsByAffiliate[r._id]?.rates;
                const pct = (bp) => (Number.isInteger(bp / 100) ? bp / 100 : (bp / 100).toFixed(2));
                const own = r.affiliateCommissionPct;
                const sub = r.subAffiliateCommissionPct;
                return (
                    <Space direction="vertical" size={2}>
                        {rates ? (
                            <Space size={4} wrap>
                                <Tag color="blue" style={{ marginInlineEnd: 0 }}>Travel {pct(rates.travel)}%</Tag>
                                <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>Language {pct(rates.language)}%</Tag>
                                <Tag color="cyan" style={{ marginInlineEnd: 0 }}>eSIM {pct(rates.esim)}%</Tag>
                            </Space>
                        ) : (
                            <Typography.Text type="secondary">—</Typography.Text>
                        )}
                        {/* The legacy pair, kept but visibly subordinate: "Own
                            page" is Book sales through this affiliate's own
                            catalogue page in THIS panel, and "Sub" is what
                            their parent takes from their sales. Different
                            channels from the three above, on their own rates. */}
                        {(own != null || sub != null) && (
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                Own page {own != null ? `${own}%` : '—'} · Sub {sub != null ? `${sub}%` : '—'}
                            </Typography.Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Active Links',
            key: 'links',
            width: 100,
            render: (_, r) => linkCounts[r._id] || 0,
        },
        {
            title: 'Lifetime Earned',
            key: 'le',
            width: 130,
            render: (_, r) => {
                // Minor units in EUR, which is what the programme pays in —
                // the old figure was labelled in dollars and came from a
                // ledger that only covered one of the two sales channels.
                const v = payoutsByAffiliate[r._id]?.lifetimeMinor || 0;
                return `€${(v / 100).toFixed(2)}`;
            },
        },
        {
            title: 'Payable Now',
            key: 'pp',
            width: 130,
            render: (_, r) => {
                // Settled and payable, not everything still inside the 30-day
                // window: "pending payout" is what we could actually pay today.
                const v = payoutsByAffiliate[r._id]?.payableMinor || 0;
                return <Tag color={v > 0 ? 'orange' : 'default'}>{`€${(v / 100).toFixed(2)}`}</Tag>;
            },
        },
        {
            title: 'Is Login',
            dataIndex: 'isLogin',
            key: 'isLogin',
            render: (val) => val ? 'Yes' : 'No'
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (val) => moment(val).format('YYYY-MM-DD HH:mm')
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Flex>
                    <Button type="link" onClick={() => handleView(record)}>
                        <FaEye />
                    </Button>
                    <Button type="link" onClick={() => handleEdit(record)} title="Edit Affiliate">
                        <FaEdit />
                    </Button>
                    <Button type="link" onClick={() => handleExtendSubscription(record)} title="Extend Subscription">
                        <FaClock />
                    </Button>
                    <Button type="link" onClick={() => handleHotelManagement(record._id)} title="Manage Clients">
                        👥
                    </Button>
                    <Button type="link" danger onClick={() => confirmDelete(record._id)}>
                        <FaTrashAlt />
                    </Button>
                </Flex>
            ),
        },
    ];

    const handleEdit = (affiliate) => {
        setSelectedAffiliate(affiliate);
        setPopupType('Edit');
        setVisible(true);
    };

    const onAddAffiliate = () => {
        setPopupType('Add');
        setSelectedAffiliate(null);
        setVisible(true);
    };

    const onSaveAffiliate = () => {
        fetchAffiliates();
        setVisible(false);
    };

    /**
     * Email every affiliate at once.
     *
     * Always previews first. This goes to every partner on the list and cannot
     * be recalled, so the confirmation names the exact number and quotes a few
     * addresses — a count alone does not let you notice that it is about to
     * mail the wrong set.
     */
    const broadcast = async (kind) => {
        const label = kind === 'invite' ? 'partner invitation' : 'password reset';
        setBroadcasting(kind);
        try {
            const preview = await ApiService.broadcastToAffiliates(kind, { dryRun: true });
            if (!preview || !preview.success) {
                message.error((preview && preview.message) || 'Could not work out who to email.');
                return;
            }
            if (!preview.wouldSend) {
                message.info('No affiliate has a login account to email.');
                return;
            }
            Modal.confirm({
                title: `Send the ${label} to ${preview.wouldSend} affiliate${preview.wouldSend === 1 ? '' : 's'}?`,
                width: 520,
                content: (
                    <div>
                        <p className="mb-2">
                            {kind === 'invite'
                                ? 'Each partner gets their commission rates and a link to set a password, valid for 7 days.'
                                : 'Each partner gets a link to choose a new password, valid for 60 minutes.'}
                        </p>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            For example: {(preview.sample || []).join(', ')}
                            {preview.wouldSend > (preview.sample || []).length ? ' …' : ''}
                        </Typography.Text>
                        {preview.skipped && preview.skipped.length > 0 && (
                            <p className="mt-2 mb-0">
                                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                                    {preview.skipped.length} skipped — no account email.
                                </Typography.Text>
                            </p>
                        )}
                    </div>
                ),
                okText: `Send ${preview.wouldSend} email${preview.wouldSend === 1 ? '' : 's'}`,
                onOk: async () => {
                    const res = await ApiService.broadcastToAffiliates(kind);
                    if (!res || !res.success) {
                        message.error((res && res.message) || 'Could not send the emails.');
                        return;
                    }
                    const failed = (res.failed || []).length;
                    // The failures are named, not just counted: "3 failed" with
                    // no addresses cannot be acted on.
                    if (failed) {
                        message.warning(`Sent ${res.sent}. ${failed} failed: ${res.failed.slice(0, 3).map((f) => f.email).join(', ')}${failed > 3 ? '…' : ''}`, 8);
                    } else {
                        message.success(`Sent the ${label} to ${res.sent} affiliate${res.sent === 1 ? '' : 's'}.`);
                    }
                },
            });
        } catch (e) {
            message.error(e?.response?.data?.message || 'Could not send the emails.');
        } finally {
            setBroadcasting(null);
        }
    };

    return (
        <PageTourWrapper pageName={TOUR_PAGES.AFFILIATES}>
            <div>
                <Flex justify="space-between" align="center" className="mb-2">
                    <div>
                        <Typography.Title level={4} className="my-0 fw-500">
                            Affiliate Management
                        </Typography.Title>
                    </div>
                    <Space>
                        {/* Two separate buttons rather than one with a menu:
                            they send different emails to the same people and
                            the difference matters, so neither should be a
                            second click behind the other. */}
                        <Button
                            size="large"
                            loading={broadcasting === 'invite'}
                            onClick={() => broadcast('invite')}
                        >
                            Invite all to earn
                        </Button>
                        <Button
                            size="large"
                            loading={broadcasting === 'reset'}
                            onClick={() => broadcast('reset')}
                        >
                            Send password resets
                        </Button>
                        <Button
                            className="custom-primary-btn affiliates-add-button"
                            type="primary"
                            size="large"
                            onClick={onAddAffiliate}
                        >
                            <Flex gap="small" align="center">
                                <span>Create Affiliate</span>
                                <HiOutlineUpload size={20} color="#fff" />
                            </Flex>
                        </Button>
                    </Space>
                </Flex>
            </div>
            <Table
                    size="middle"
                    className="custom_table affiliates-table"
                    bordered
                    columns={columns}
                    dataSource={affiliates}
                    loading={tableLoading}
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 10 }}
                    rowKey="_id"
                />
            <AffiliatePopup
                open={visible}
                setOpen={() => setVisible(false)}
                onSaveAffiliate={onSaveAffiliate}
                categories={categories}
                affiliate={selectedAffiliate}
                type={popupType}
            />

            {/* QR Code Modal */}
            <Modal
                title="QR Code - Affiliate Guide Link"
                open={qrModalVisible}
                onCancel={() => setQrModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setQrModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={400}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <QRCode
                        value={selectedQrUrl}
                        size={300}
                        level="M"
                        includeMargin={true}
                    />
                    <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <Typography.Text style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                            {selectedQrUrl}
                        </Typography.Text>
                    </div>
                </div>
            </Modal>

            {/* Extend Subscription Modal */}
            <ExtendSubscriptionModal
                open={extendModalVisible}
                onClose={() => setExtendModalVisible(false)}
                affiliate={selectedAffiliateForExtend}
                onExtendSuccess={onExtendSuccess}
            />
        </PageTourWrapper>
    );
};

export default AffiliateManagement;
